import { MAX_NOTES_LENGTH } from '@core/types';
import {
  normalizeGroupIds,
  normalizeLogoutUris,
  normalizeRedirectUris,
  ValidationError,
} from '@core/validation';
import { useState } from 'react';
import { UnrestrictedCallout } from './AccessBadge';
import { GroupPicker } from './GroupPicker';

export interface AppFormValues {
  appName: string;
  redirectUris: string[];
  logoutUris: string[];
  requiredGroupIds: string[];
  notes: string;
}

interface AppFormProps {
  initial: AppFormValues;
  /** App names are immutable - Cognito's ClientName and the registry's GSI key both derive from it. */
  nameEditable: boolean;
  submitLabel: string;
  busy: boolean;
  onSubmit: (values: AppFormValues) => void;
}

/** Splits a textarea into non-empty trimmed lines. */
function lines(value: string): string[] {
  return value
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
}

/**
 * Runs a core normalizer for preview purposes, returning either the normalized values or the exact
 * error message the API would produce. Using the same functions the server uses is the whole point
 * of src/core/validation.ts being dependency-free - a reimplementation here would drift.
 */
function preview<T>(fn: (input: T) => string[], input: T): { values?: string[]; error?: string } {
  try {
    return { values: fn(input) };
  } catch (err) {
    if (err instanceof ValidationError) return { error: err.message };
    throw err;
  }
}

export function AppForm({ initial, nameEditable, submitLabel, busy, onSubmit }: AppFormProps) {
  const [appName, setAppName] = useState(initial.appName);
  const [redirectText, setRedirectText] = useState(initial.redirectUris.join('\n'));
  const [logoutText, setLogoutText] = useState(initial.logoutUris.join('\n'));
  const [groupIds, setGroupIds] = useState(initial.requiredGroupIds);
  const [notes, setNotes] = useState(initial.notes);

  const redirectUris = lines(redirectText);
  const logoutUris = lines(logoutText);

  const redirectPreview = preview(normalizeRedirectUris, redirectUris);
  const logoutPreview = preview(normalizeLogoutUris, logoutUris);
  const groupPreview = preview(normalizeGroupIds, groupIds);

  const isOpenToEveryone = groupIds.length === 0;
  // The API applies the same rule (guards.ts assertUnrestrictedIsExplained) and is the one that
  // counts; this gates submit so the operator learns it while the form is in front of them.
  const notesRequired = isOpenToEveryone && !notes.trim();
  const blocked =
    busy ||
    !!redirectPreview.error ||
    !!logoutPreview.error ||
    !!groupPreview.error ||
    notesRequired;

  return (
    <form
      className="stack"
      onSubmit={(event) => {
        event.preventDefault();
        if (blocked) return;
        onSubmit({
          appName: appName.trim(),
          redirectUris,
          logoutUris,
          requiredGroupIds: groupIds,
          notes: notes.trim(),
        });
      }}
    >
      <label className="field">
        <span>App name</span>
        <input
          value={appName}
          onChange={(e) => setAppName(e.target.value)}
          disabled={!nameEditable || busy}
          placeholder="forward-summit"
          autoComplete="off"
        />
        <span className="hint">
          {nameEditable
            ? 'Lowercase letters, digits and hyphens.'
            : 'The app name cannot be changed after creation.'}
        </span>
      </label>

      <label className="field">
        <span>Redirect URIs</span>
        <textarea
          value={redirectText}
          onChange={(e) => setRedirectText(e.target.value)}
          disabled={busy}
          rows={4}
          placeholder="https://forward-summit.replit.app/auth/callback"
        />
        <span className="hint">One per line. https only, except localhost. No fragments.</span>
      </label>

      {/*
        Shows the normalized form before submission. new URL().toString() appends a trailing slash
        to a bare origin, and Cognito matches redirect_uri as an exact string - so seeing the change
        here beats discovering it as a redirect_mismatch after saving.
      */}
      {redirectPreview.error ? (
        <div className="callout callout-error">{redirectPreview.error}</div>
      ) : (
        redirectPreview.values &&
        redirectPreview.values.join('\n') !== redirectUris.join('\n') && (
          <div className="callout">
            <strong>Will be stored as:</strong>
            <ul className="plain">
              {redirectPreview.values.map((uri) => (
                <li key={uri}>
                  <code>{uri}</code>
                </li>
              ))}
            </ul>
          </div>
        )
      )}

      <label className="field">
        <span>Logout URIs</span>
        <textarea
          value={logoutText}
          onChange={(e) => setLogoutText(e.target.value)}
          disabled={busy}
          rows={2}
          placeholder="https://forward-summit.replit.app/signed-out"
        />
        <span className="hint">
          Optional, one per line, same rules as above. Where the broker may return the browser after
          sign-out. Without one, the app can drop its own tokens but cannot end the broker session -
          signing back in then needs no password.
        </span>
      </label>

      {logoutPreview.error ? (
        <div className="callout callout-error">{logoutPreview.error}</div>
      ) : (
        logoutPreview.values &&
        logoutPreview.values.join('\n') !== logoutUris.join('\n') && (
          <div className="callout">
            <strong>Will be stored as:</strong>
            <ul className="plain">
              {logoutPreview.values.map((uri) => (
                <li key={uri}>
                  <code>{uri}</code>
                </li>
              ))}
            </ul>
          </div>
        )
      )}

      {/*
        The picker only ever yields object IDs - either chosen from a search result or pasted - so
        the preview below is a backstop against a malformed paste, not the primary check.
      */}
      <GroupPicker value={groupIds} onChange={setGroupIds} disabled={busy} />

      {groupPreview.error && <div className="callout callout-error">{groupPreview.error}</div>}

      {/*
        Clearing the restriction stays possible - it is a legitimate configuration - but it cannot
        happen by leaving a field blank and not noticing: with no groups, submit is gated on the
        operator writing down why.
      */}
      {isOpenToEveryone && <UnrestrictedCallout />}

      <label className="field">
        <span>Notes{isOpenToEveryone && <span className="req"> (required)</span>}</span>
        <textarea
          className="prose"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={busy}
          rows={3}
          maxLength={MAX_NOTES_LENGTH}
          placeholder={
            isOpenToEveryone
              ? 'Why does this app have no group restriction?'
              : 'What this app is, who owns it, anything the next operator should know.'
          }
          aria-invalid={notesRequired}
        />
        <span className="hint">
          {notesRequired
            ? 'This app has no required groups, so notes explaining why are required.'
            : 'Free text. Shown on the app page and stored with the registry row.'}
        </span>
      </label>

      <div className="actions">
        <button type="submit" className="primary" disabled={blocked}>
          {busy ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
